import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Image,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { LOGIN } from '#src/graphql/mutations/login';
import { useUser } from '#src/providers/user-provider';

export function LoginPage() {
  const { refetchMe } = useUser();
  const [loginMutation, { loading: mutationLoading }] = useMutation(LOGIN, {
    errorPolicy: 'all',
  });
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const attemptLogin = async () => {
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    try {
      const result = await loginMutation({
        variables: {
          input: { email: trimmedEmail, password },
        },
      });
      const user = result.data?.login?.user;
      if (user) {
        await refetchMe();
        toaster.create({
          title: 'Welcome',
          description: `Signed in as ${user.email}`,
          type: 'success',
          duration: 3000,
          closable: true,
        });
      } else {
        setError('Invalid email or password.');
      }
    } catch {
      setError('Invalid email or password.');
    }
  };

  return (
    <Center minH="100vh" bg="bg.subtle" p={4}>
      <Box bg="bg" shadow="lg" borderRadius="lg" p={8} w="full" maxW="md">
        <VStack gap={6} align="stretch">
          <VStack gap={1}>
            <HStack gap={3} align="center">
              <Image src="/icon.svg" alt="Note Plan Logo" w={8} h={8} />
              <Heading size="lg" color="blue.600">
                Note Plan
              </Heading>
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              Sign in with your email and password
            </Text>
          </VStack>
          <Box>
            <Input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') attemptLogin();
              }}
              placeholder="you@example.com"
              type="email"
              size="lg"
              autoComplete="email"
              autoFocus
              aria-invalid={!!error}
              mb={3}
            />
            <Input
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') attemptLogin();
              }}
              placeholder="Password"
              type="password"
              size="lg"
              autoComplete="current-password"
              aria-invalid={!!error}
              aria-describedby={error ? 'login-error' : undefined}
            />
            {error && (
              <Text
                id="login-error"
                mt={2}
                fontSize="sm"
                color="red.600"
                role="alert"
              >
                {error}
              </Text>
            )}
          </Box>
          <Button
            type="button"
            colorScheme="blue"
            size="lg"
            width="full"
            onClick={() => void attemptLogin()}
            disabled={mutationLoading}
          >
            {mutationLoading ? <Spinner size="sm" /> : 'Sign in'}
          </Button>
        </VStack>
      </Box>
    </Center>
  );
}
